import React, { useEffect, useState } from 'react';
import { Link } from "react-router-dom";
import Select from "react-select";
import { Button } from "react-bootstrap";
import swal from "sweetalert2";
import ItemCategory from '../../../components/ProductQuality/ItemCategory';
import ItemSubcategory from '../../../components/ProductQuality/ItemSubCategory';
import ItemRow from '../../../components/ProductQuality/ItemRow';
import axios from 'axios';

const ManageItemQuantity = () => {
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  const [data, setData] = useState([]);
  // const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductObj, setSelectedProductObj] = useState(null);

  const [itemStates, setItemStates] = useState({});
  const [generateClicked, setGenerateClicked] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);


  useEffect(() => {
    // Assume branch_id is stored in localStorage or from state/context
    const branchId = localStorage.getItem('branch_id'); // or get it from props/state

    // Build URL with branch_id query param if exists
    const url = new URL(`${baseURL}/products/grouped-by-product-item-category`);
    if (branchId) {
      url.searchParams.append('branch_id', branchId);
    }

    fetch(url.toString())
      .then(res => res.json())
      .then(json => {
        setData(json.data || []);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message || 'Failed to load data');
        setLoading(false);
      });
  }, [baseURL]);


  // Options for Select inputs
  const productCategoryOptions = data.map(category => ({
    value: category.product_category_id.toString(),
    label: category.product_category_name,
  }));

  const productOptions = selectedCategory
    ? (data.find(cat => cat.product_category_id.toString() === selectedCategory)?.products.map(prod => ({
      value: prod.product_id.toString(),
      label: prod.product_name,
    })) || [])
    : [];

  // Handlers for Select inputs
  const handleCategoryChange = selectedOption => {
    setSelectedCategory(selectedOption?.value || null);
    setSelectedProduct(null);
    setSelectedProductObj(null);
    setItemStates({});
    setGenerateClicked(false);
    setIsUpdateMode(false);
  };

  const handleProductChange = selectedOption => {
    const productId = selectedOption?.value || null;
    setSelectedProduct(productId);
    setGenerateClicked(false);
    setIsUpdateMode(false);

    // Find product object from loaded data
    const product = data.find(cat => cat.product_category_id.toString() === selectedCategory)
      ?.products.find(p => p.product_id.toString() === productId);
    if (product) {
      setSelectedProductObj(product);
    } else {
      setSelectedProductObj(null);
    }
  };

  // Generate item states from backend data + product object
  const onGenerate = async (productObj) => {
    if (!selectedCategory || !selectedProduct || !productObj) return;

    setGenerateClicked(true);

    try {
      // Fetch existing saved item quantities and category counts
      const [itemRes, categoryRes] = await Promise.all([
        fetch(`${baseURL}/productitemquantity/${selectedProduct}`),
        fetch(`${baseURL}/get-product-item-quantity/${selectedProduct}`)
      ]);

      const itemResult = await itemRes.json();
      const categoryResult = await categoryRes.json();

      const existingItems = itemResult?.data?.item_quantity || [];
      const existingCategoryCounts = categoryResult?.data || [];

      const newItemStates = {};

      // Initialize all items with zero quantities and all category/subcategory counts to 0
      productObj.item_categories?.forEach(category => {
        category.items?.forEach(item => {
          newItemStates[item.item_id] = { quantity: 0 };
        });

        category.item_sub_categories?.forEach(sub => {
          newItemStates[`input_sub_${sub.item_sub_category_id}`] = { value: '0' };
        });

        newItemStates[`input_${category.item_category_id}`] = { value: '0' };
      });

      // Override with existing saved item quantities
      existingItems.forEach(item => {
        newItemStates[item.item_id] = { quantity: item.quantity ?? 0 };
      });

      // Override with existing saved category + subcategory counts
      existingCategoryCounts.forEach(cat => {
        newItemStates[`input_${cat.item_category_id}`] = { value: (cat.count ?? 0).toString() };

        cat.subcategories?.forEach(sub => {
          newItemStates[`input_sub_${sub.itemsubcategory_id}`] = { value: (sub.count ?? 0).toString() };
        });
      });

      setItemStates(newItemStates);
      setIsUpdateMode(existingItems.length > 0 || existingCategoryCounts.length > 0);
    } catch (err) {
      console.error("Fetch error:", err);
      setItemStates({});
      setIsUpdateMode(false);
      setGenerateClicked(false);
    }
  };

  // Input validation for numeric fields (up to 6 digits)
  const validateNumber = (val) => /^[0-9]{0,6}$/.test(val);

  // Handler for item quantity changes
  const handleQuantityChange = (itemId, value) => {
    if (!validateNumber(value)) return;

    setItemStates(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], quantity: parseInt(value, 10) || 0 }
    }));
  };

  // Handler for subcategory count changes and auto-updating category count total
  const handleSubcategoryCountChange = (category, subcategoryId, newValue) => {
    if (!validateNumber(newValue)) return;

    setItemStates(prev => {
      const updated = {
        ...prev,
        [`input_sub_${subcategoryId}`]: {
          ...prev[`input_sub_${subcategoryId}`],
          value: newValue
        }
      };

      // Recalculate total category count as sum of all subcategory counts
      const totalCount = category.item_sub_categories.reduce((sum, sub) => {
        const key = `input_sub_${sub.item_sub_category_id}`;
        const val = key === `input_sub_${subcategoryId}` ? newValue : (prev[key]?.value ?? '0');
        const parsed = parseInt(val, 10);
        return sum + (isNaN(parsed) ? 0 : parsed);
      }, 0);

      updated[`input_${category.item_category_id}`] = { value: totalCount.toString() };

      return updated;
    });
  };

  const branchId = localStorage.getItem('branch_id');
  const userId = localStorage.getItem('user_id');

  const handleSubmit = async () => {
    if (!selectedProduct) {
      swal.fire("Validation", "Please select a product.", "warning");
      return;
    }

    // Validate localStorage data
    if (!branchId || !userId) {
      swal.fire("Error", "Missing branch or user info. Please log in again.", "error");
      return;
    }

    // --- Payload for item quantity ---
    const item_quantity_payload = {
      product_id: Number(selectedProduct),
      item_quantity: Object.entries(itemStates)
        .filter(([key]) => !key.startsWith('input_'))
        .map(([item_id, val]) => ({
          item_id: Number(item_id),
          quantity: val.quantity ?? 0,
        })),
      branch_id: Number(branchId),     // ✅ Added
      created_by: Number(userId),      // ✅ Added
    };

    // --- Payload for category + subcategory counts ---
    const item_category_list_payload = {
      product_id: Number(selectedProduct),
      item_category_list: selectedProductObj?.item_categories?.map(cat => {
        const subcategories = cat.item_sub_categories?.map(sub => {
          const count = Number(itemStates[`input_sub_${sub.item_sub_category_id}`]?.value ?? '0');
          return {
            itemsubcategory_id: sub.item_sub_category_id,
            count,
          };
        }) || [];

        const totalCount = subcategories.reduce((sum, sub) => sum + sub.count, 0);

        return {
          item_category_id: cat.item_category_id,
          count: totalCount,
          subcategories,
        };
      }) || [],
      branch_id: Number(branchId),     // ✅ Added
      created_by: Number(userId),      // ✅ Added
    };

    try {
      // Save item quantities
      const res1 = await fetch(`${baseURL}/productitemquantity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item_quantity_payload),
      });

      const json1 = await res1.json();
      if (!res1.ok) throw new Error(json1.message || 'Item quantity submission failed');

      // Save category + subcategory counts
      const res2 = await fetch(`${baseURL}/save-product-item-quantity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item_category_list_payload),
      });

      const json2 = await res2.json();
      if (!res2.ok) throw new Error(json2.message || 'Category count submission failed');

      setIsUpdateMode(true);
      swal.fire("Success", "Quantities saved successfully!", "success");
    } catch (err) {
      swal.fire("Error", err.message || "Something went wrong", "error");
    }
  };

  const [hover, setHover] = useState(false);
  // if (loading) return <p>Loading...</p>;
  // if (error) return <p>Error: {error}</p>;

  const [isMaintenance, setIsMaintenance] = useState(false);

  const branch_id = parseInt(localStorage.getItem("branch_id"), 10) || 1;
  const [loading, setLoading] = useState(true);
  // --- Check maintenance ---
  useEffect(() => {
    let isMounted = true;

    const checkMaintenanceStatus = async () => {
      try {
        const response = await axios.get(
          `${baseURL}/maintenance-mode?branch_id=${branch_id}`
        );
        const data = response.data.data || [];
        const branchStatus = data.find(
          (item) => item.slug === "manage-product-quantity"
        );
        if (isMounted) setIsMaintenance(branchStatus?.maintenancemode_status === 1);
      } catch (error) {
        console.error(error);
        if (isMounted) setIsMaintenance(false);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    checkMaintenanceStatus();

    return () => {
      isMounted = false;
    };
  }, [baseURL, branch_id]);

  // --- Conditional rendering after hooks ---
  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (isMaintenance) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center text-center" style={{ height: "100vh", width: "100vw" }}>
        <h1 className="text-warning mb-3">🚧 Under Maintenance</h1>
        <p className="text-muted fs-5">
          The <strong>Manage Product Quantity</strong> module is currently under maintenance.<br />
          Please check back later.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between page-titles px-0 py-1 mx-0">
        <div className='mb-0'>
          <h4 className='breadcrumb-header'>Manage Product Quantity</h4>
          <ol className="breadcrumb fs-7">
            <li className="breadcrumb-item"><Link to='/dashboard'>Dashboard</Link></li>
            <li className="breadcrumb-item"><i className="fa-solid fa-angles-right fs-8"></i></li>
            <li className="breadcrumb-item active">Package Management</li>
          </ol>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-lg-3">
          <label className='text-black fs-6 font-w500'>Product Category</label>
          <Select
            options={productCategoryOptions}
            value={productCategoryOptions.find(opt => opt.value === selectedCategory) || null}
            onChange={handleCategoryChange}
            placeholder="Select Category"
          />
        </div>
        <div className="col-lg-3">
          <label className='text-black fs-6 font-w500'>Product</label>
          <Select
            options={productOptions}
            value={productOptions.find(opt => opt.value === selectedProduct) || null}
            onChange={handleProductChange}
            isDisabled={!selectedCategory}
            placeholder="Select Product"
          />
        </div>
        {selectedProduct && (
          <div className="col-lg-4 mt-4 d-flex align-items-center justify-content-start gap-1">
            <Button
              className="me-2 btn-sm"
              variant="primary"
              onClick={() => onGenerate(selectedProductObj)}
            >
              Apply
            </Button>
            <Button className="btn-sm" variant="" onClick={() => window.location.reload()} style={{ backgroundColor: hover ? '#fdc8cd' : '#025d3a2a' }} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
              <span className='font-w400 text-primary'><i class="fa-solid fa-arrows-rotate" style={{ color: hover ? '#dc3545' : '' }}></i></span>
            </Button>
          </div>
        )}
      </div>

      {selectedProductObj && generateClicked && (
        <div className="row mx-1 mb-2 border py-2 rounded">
          {selectedProductObj.item_categories.map(cat => (
            <div className="col-lg-12 border-bottom mb-3" key={cat.item_category_id}>
              <ItemCategory title={cat.item_category_name}>
                {cat.item_sub_categories.map(sub => (
                  <ItemSubcategory
                    key={sub.item_sub_category_id}
                    title={sub.item_sub_category_name}
                    itemCountInput={
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className="form-control form-control-sm"
                        placeholder="Enter count"
                        maxLength={6}
                        value={itemStates[`input_sub_${sub.item_sub_category_id}`]?.value ?? '0'}
                        onChange={(e) =>
                          handleSubcategoryCountChange(cat, sub.item_sub_category_id, e.target.value)
                        }
                      />
                    }
                  >
                    {sub.items.map(item => (
                      <ItemRow
                        key={item.item_id}
                        name={item.item_name}
                        unit={item.measurement_quantity_slug_name}
                        quantityValue={
                          itemStates[item.item_id]?.quantity !== undefined
                            ? itemStates[item.item_id].quantity
                            : ''
                        }
                        onQuantityChange={(e) =>
                          handleQuantityChange(item.item_id, e.target.value)
                        }
                      />
                    ))}
                  </ItemSubcategory>
                ))}
              </ItemCategory>
            </div>
          ))}

          <div className="d-flex align-items-center justify-content-end gap-3 mt-4 mb-4">
            <Button variant="primary" onClick={handleSubmit}>
              {isUpdateMode ? 'Update' : 'Create'} <i className="fas fa-angle-double-right ms-2"></i>
            </Button>
          </div>
        </div>
      )}
    </>
  );

};

export default ManageItemQuantity;
